import { useQuery } from "@tanstack/react-query"
import { AnimatePresence, motion } from "motion/react"
import { useMemo } from "react"

import { graphql } from "#gql"
import { queryKeys } from "#lib/query-keys.ts"
import { AircraftCard } from "#pages/authenticated/fleet/components/card/aircraft-card.tsx"
import type { FleetRow } from "#pages/authenticated/fleet/components/table/fleet-table-types.ts"
import {
  FleetTable,
  useFleetTable,
} from "#pages/authenticated/fleet/components/table/fleet-table.tsx"
import { useFleetFilterStore } from "#pages/authenticated/fleet/components/toolbar/fleet-filter-store.ts"
import { FleetToolbar } from "#pages/authenticated/fleet/components/toolbar/fleet-tool-bar.tsx"
import { useFleetViewStore } from "#pages/authenticated/fleet/components/toolbar/use-fleet-view-store.ts"
import { useLoggedInUserInfo } from "#store/user-info.ts"
import { getUserImageUrl } from "#utils/file-url.ts"
import { client } from "#utils/graphql-client.ts"

import { Empty } from "./components/empty.tsx"

export const FleetListDocument = graphql(/* GraphQL */ `
  query FleetList($input: QueryAircraftByOrganizationInput!) {
    aircraftByOrganization(input: $input) {
      id
      source
      tail
      manufacturer
      model
      yom
      category
      serial
      images
      owners {
        id
        name
      }
      keyAccountManagers {
        id
        name
        image
      }
    }
  }
`)

export const Fleet = () => {
  const { me } = useLoggedInUserInfo()
  const { view } = useFleetViewStore()
  const scope = useFleetFilterStore((s) => s.scope)

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.aircraft.list(me.organization.id),
    queryFn: () =>
      client.request(FleetListDocument, {
        input: { organizationId: me.organization.id, sources: ["LEON", "MANUAL"] },
      }),
  })

  const rows = useMemo<FleetRow[]>(
    () =>
      (data?.aircraftByOrganization ?? [])
        .filter(
          (ac) =>
            scope === "all" || ac.keyAccountManagers.some((k) => k.id === me.user.id),
        )
        .map((ac) => ({
          ...ac,
          keyAccountManagers: ac.keyAccountManagers.map((k) => ({
            ...k,
            image: getUserImageUrl(me.organization.id, k.image) ?? null,
          })),
          lastMonth: undefined,
          ytdHours: undefined,
          status: "healthy" as const,
        })),
    [data, me.organization.id, me.user.id, scope],
  )

  const table = useFleetTable(rows)
  const visibleRows = table.getRowModel().rows

  if (!isLoading && !data?.aircraftByOrganization.length) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col p-4 pt-0">
      <FleetToolbar table={table} />

      <AnimatePresence mode="wait">
        {rows.length === 0 ? (
          <motion.div
            key="none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-muted-foreground flex flex-1 flex-col items-center justify-center gap-1 text-sm"
          >
            <p className="text-foreground font-medium">No aircraft in your fleet</p>
            <p>You are not a key account manager of any aircraft yet</p>
          </motion.div>
        ) : view === "grid" ? (
          <motion.div
            key="grid"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="-m-1 grid min-h-0 flex-1 auto-rows-max grid-cols-1 gap-4 overflow-y-auto p-1 xl:grid-cols-2"
          >
            {visibleRows.map((row) => (
              <AircraftCard key={row.id} aircraft={row.original} />
            ))}
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="min-h-0 flex-1"
          >
            <FleetTable table={table} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
